import { z } from 'zod';

/**
 * Task #2640 — Coordinazione cluster delle risorse process-bound.
 *
 * Alcune risorse realtime non sono serializzabili su Redis come lo stato
 * chiamate live (Task #2638): tengono vive un `WebSocket` (sessioni AI vocali
 * Gemini Live / OpenAI Realtime) o un `NodeJS.Timeout` (digit-collection IVR,
 * reconnect/maxDuration delle sessioni AI). Questi oggetti vivono nel processo
 * del nodo backend che li ha creati e NON possono essere ricostruiti altrove.
 *
 * Per coordinarle nel cluster multi-nodo serve:
 *   1) un registry di ownership su Redis (`resourceType:resourceId -> nodeName`)
 *      con TTL + heartbeat, gestito da `process-resource-registry.service.ts`;
 *   2) un routing dei comandi al nodo proprietario via pub/sub Redis, gestito
 *      da `node-command-bus.service.ts`.
 *
 * Le enum canoniche vivono nel package shared (come `cluster-locks.ts`) per
 * evitare divergenze tra registry, command bus e i servizi che li consumano.
 */

/**
 * Tipi di risorsa process-bound tracciate nel registry di ownership.
 *
 * Convenzione naming: snake_case, singolare logico della risorsa. Il registry
 * (`process-resource-registry.service.ts`) è generico sul tipo: nuove risorse
 * cross-node non serializzabili si aggiungono qui quando hanno un consumer di
 * routing reale (non si pre-dichiarano tipi senza wiring).
 *
 * Tutte queste risorse vivono nel processo del nodo backend che le ha create
 * (WebSocket vivo o Map/`NodeJS.Timeout` in-memory). Gli eventi ESL sono
 * guidati dal nodo che tiene la subscription, MA le route HTTP/webhook sono
 * load-balanced: un comando di controllo (chiusura sessione, hangup SIP-AI,
 * stop MOH) può atterrare su un nodo ≠ owner → serve risolvere l'owner via
 * registry e instradare via command bus.
 *
 * Nota architetturale: il timer di digit-collection IVR e il conference
 * play/stop NON sono qui perché sono interamente guidati da eventi ESL sul
 * nodo owner (DTMF/timeout/hangup arrivano lì) e i comandi verso FreeSWITCH
 * passano da `getEslForTenant` che raggiunge FS direttamente da qualsiasi
 * nodo: nessun gap cross-node sull'oggetto process-bound. Si registra invece
 * `ivr_session` per ownership/osservabilità e `background_music` perché il
 * wrapper pubblico `esl.service.stopBackgroundMusic` è una superficie
 * cross-node reale.
 */
export const PROCESS_RESOURCE_TYPES = {
  /** Sessione AI vocale con `WebSocket` vivo (Gemini Live, OpenAI Realtime). */
  ai_voice_session: 'ai_voice_session',
  /** OpenAI SIP monitor, distinct from Gemini and ESL-owned SIP-AI sessions. */
  openai_sip_session: 'openai_sip_session',
  /** Sessione IVR con Map stato + `NodeJS.Timeout` digit-collection (id = FS UUID). */
  ivr_session: 'ivr_session',
  /** Sessione SIP-AI tracciata via eventi ESL (id = call_id). */
  sip_ai_session: 'sip_ai_session',
  /** Background music (MOH) attiva su un canale (id = call UUID). */
  background_music: 'background_music',
} as const;

export type ProcessResourceType =
  (typeof PROCESS_RESOURCE_TYPES)[keyof typeof PROCESS_RESOURCE_TYPES];

/**
 * Nomi canonici dei comandi instradabili al nodo proprietario.
 *
 * Convenzione naming: `area.action`. Ogni comando ha un handler tipizzato
 * registrato via `nodeCommandBus.registerCommandHandler(kind, schema, fn)`,
 * dove `schema` (Zod) valida il payload al boundary cross-nodo (no `any`).
 */
export const NODE_COMMAND_KINDS = {
  /** Internal HMAC-authenticated FreeSWITCH hangup; not an operator command. */
  ai_voice_close: 'ai_voice.close',
  /** Gemini operator commands repeat JWT/tenant/permission checks on the owner. */
  gemini_live_read: 'gemini_live.read',
  gemini_live_close: 'gemini_live.close',
  openai_sip_read: 'openai_sip.read',
  openai_sip_close: 'openai_sip.close',
  /** Forza l'hangup di una sessione SIP-AI sul nodo owner (id = call_id). */
  sip_ai_hangup: 'sip_ai.hangup',
  /** Ferma la background music (MOH) sul nodo owner del canale (id = call UUID). */
  background_music_stop: 'background_music.stop',
} as const;

export type NodeCommandKind =
  (typeof NODE_COMMAND_KINDS)[keyof typeof NODE_COMMAND_KINDS];

/** Safe administrative projection: never serialize sockets, keys or transcripts. */
export const GeminiLiveSessionSummarySchema = z.object({
  sessionId: z.string(),
  callId: z.string(),
  agentId: z.string(),
  channel: z.enum(['sip', 'webrtc']),
  callerNumber: z.string().optional(),
  startedAt: z.number(),
  durationSeconds: z.number().int().nonnegative(),
  isConnected: z.boolean(),
}).strict();
export type GeminiLiveSessionSummary = z.infer<typeof GeminiLiveSessionSummarySchema>;
